import React from 'react';

import { Button } from '@/design-system/button';
import { Input, InputRoot } from '@/design-system/input';
import { Popover, PopoverTrigger } from '@/design-system/popover';
import { FilterPopoverContent } from '@/components/FilterPopover';
import { SearchSelect } from '@/design-system/search-select';
import { AgingTier, invoicesAPI } from '@/services/api/invoices';
import { useSearchableOptions } from './useSearchableOptions';

export type DatePreset = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days';

export interface InvoiceFilterValue {
  agingTier: AgingTier[];
  datePreset: DatePreset | '';
  issuedFrom: string;
  issuedTo: string;
  clientId: string;
  outletId: string;
  hubId: string;
}

export const EMPTY_FILTERS: InvoiceFilterValue = {
  agingTier: [],
  datePreset: '',
  issuedFrom: '',
  issuedTo: '',
  clientId: '',
  outletId: '',
  hubId: '',
};

export const countActiveFilters = (v: InvoiceFilterValue) =>
  Number(v.agingTier.length > 0) +
  Number(Boolean(v.datePreset || v.issuedFrom || v.issuedTo)) +
  Number(Boolean(v.clientId)) +
  Number(Boolean(v.outletId)) +
  Number(Boolean(v.hubId));

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

const AGING_OPTIONS: {
  key: AgingTier;
  label: string;
  dot: string;
}[] = [
  { key: 'green', label: 'Green · 0-3 days', dot: 'bg-green-500' },
  { key: 'amber', label: 'Amber · 4-6 days', dot: 'bg-amber-500' },
  // { key: 'orange', label: 'Orange · 7-9 days', dot: 'bg-orange-500' },
  { key: 'red', label: 'Red · 7+ days', dot: 'bg-red-500' },
];

interface FilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeFilterCount: number;
  value: InvoiceFilterValue;
  onChange: (next: InvoiceFilterValue) => void;
  onApply: () => void;
  onReset: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  open,
  onOpenChange,
  activeFilterCount,
  value,
  onChange,
  onApply,
  onReset,
}) => {
  const clients = useSearchableOptions(open, invoicesAPI.clients);
  const outlets = useSearchableOptions(open, invoicesAPI.outlets);
  const hubs = useSearchableOptions(open, invoicesAPI.hubs);

  const updateDatePreset = (preset: DatePreset) => {
    onChange({
      ...value,
      datePreset: value.datePreset === preset ? '' : preset,
      issuedFrom: '',
      issuedTo: '',
    });
  };

  const toggleAging = (tier: AgingTier) => {
    onChange({
      ...value,
      agingTier: value.agingTier.includes(tier)
        ? value.agingTier.filter((t) => t !== tier)
        : [...value.agingTier, tier],
    });
  };

  const labelCls = 'mb-2 block text-[11px] font-medium text-text-sub-600';

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button tone="neutral" style="stroke" size="sm">
          <i className="ri-equalizer-2-line mr-1.5 text-[14px]" />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-1.5 rounded-md bg-bg-strong-950 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-text-white-0">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <FilterPopoverContent
        align="end"
        className="w-[360px] p-0"
        aria-label="Filter invoices"
      >
        <div className="flex items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            Filter invoices
          </p>
          <button
            onClick={onReset}
            className="text-[11px] font-medium text-text-sub-600 hover:text-accent transition-colors"
          >
            Reset
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-4 px-4 py-3">
        <div>
          <p className={labelCls}>Date</p>
          <div className="flex flex-wrap gap-1">
            {DATE_PRESETS.map((p) => {
              const active = value.datePreset === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => updateDatePreset(p.key)}
                  className={`rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors ${
                    active
                      ? 'border-accent text-accent'
                      : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <InputRoot size="sm" className="flex-1">
              <Input
                type="date"
                value={value.issuedFrom}
                onChange={(e) =>
                  onChange({
                    ...value,
                    issuedFrom: e.target.value,
                    datePreset: '',
                  })
                }
              />
            </InputRoot>
            <span className="text-xs text-text-soft-400">to</span>
            <InputRoot size="sm" className="flex-1">
              <Input
                type="date"
                value={value.issuedTo}
                onChange={(e) =>
                  onChange({
                    ...value,
                    issuedTo: e.target.value,
                    datePreset: '',
                  })
                }
              />
            </InputRoot>
          </div>
        </div>

        <div>
          <p className={labelCls}>Aging tier</p>
          <div className="flex flex-col gap-1">
            {AGING_OPTIONS.map((opt) => {
              const active = value.agingTier.includes(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => toggleAging(opt.key)}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm tracking-tight transition-colors ${
                    active
                      ? 'bg-bg-weak-50 text-text-strong-950 font-semibold'
                      : 'text-text-sub-600 hover:bg-bg-weak-50'
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                  {opt.label}
                  {active && (
                    <i className="ri-check-line ml-auto text-[14px] text-accent" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={labelCls + ' block'}>Client</label>
          <SearchSelect
            placeholder="All clients"
            options={clients.options.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            value={value.clientId}
            selectedLabelFallback={clients.labelFor(value.clientId)}
            onChange={(next) => onChange({ ...value, clientId: next })}
            onSearchChange={clients.setSearch}
            disableLocalFilter
            loading={clients.loading}
            emptyMessage={clients.error ?? 'No clients found'}
          />
        </div>

        <div>
          <label className={labelCls + ' block'}>Recipient</label>
          <SearchSelect
            placeholder="All recipients"
            options={outlets.options.map((o) => ({
              value: o.id,
              label: o.name,
            }))}
            value={value.outletId}
            selectedLabelFallback={outlets.labelFor(value.outletId)}
            onChange={(next) => onChange({ ...value, outletId: next })}
            onSearchChange={outlets.setSearch}
            disableLocalFilter
            loading={outlets.loading}
            emptyMessage={outlets.error ?? 'No recipients found'}
          />
        </div>

        <div>
          <label className={labelCls + ' block'}>Hub</label>
          <SearchSelect
            placeholder="All hubs"
            options={hubs.options.map((h) => ({
              value: h.id,
              label: h.name,
            }))}
            value={value.hubId}
            selectedLabelFallback={hubs.labelFor(value.hubId)}
            onChange={(next) => onChange({ ...value, hubId: next })}
            onSearchChange={hubs.setSearch}
            disableLocalFilter
            loading={hubs.loading}
            emptyMessage={hubs.error ?? 'No hubs found'}
          />
        </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={onApply}>
            Apply
          </Button>
        </div>
      </FilterPopoverContent>
    </Popover>
  );
};

export default FilterPanel;
